import type { ReactNode } from 'react'

/**
 * The mutually-exclusive "sources" a media (icon/image) field can draw from. Rendered as an antd
 * `Radio.Group` (button style) when a field declares 2+ sources (see `TemplateMediaField`).
 *
 *  - `upload`        — a static uploaded image (opens the Pintura editor on tile click)
 *  - `domain-default`— the domain's configured default icon (read-only; `is_using_domain_default_icon`)
 *  - `dynamic-macro` — a per-subscriber Liquid macro, e.g. `{{profile.home_market_icon_url}}`
 *  - `fixed-macro`   — a single predetermined macro, e.g. the image field's `{{item.image}}`
 *  - `none`          — explicitly no icon
 */
export type MediaFieldSourceKind = 'upload' | 'domain-default' | 'dynamic-macro' | 'fixed-macro' | 'none'

/**
 * Placeholder value carried while the `dynamic-macro` source is selected but the user hasn't entered a
 * macro yet. It keeps the source selected and shows the "dynamic" preview, but is treated as empty:
 * `DynamicMacroField` renders it as a blank input, and it is stripped to `null` before save so an empty
 * macro never reaches a live notification.
 */
export const EMPTY_MACRO_SENTINEL = '{{}}'

export interface MediaFieldSource {
    key: MediaFieldSourceKind
    label: ReactNode
    /** Leading segment icon (12px). */
    icon?: ReactNode
    /** Hover description for the segment (keeps static helper text out of the form's vertical flow). */
    tooltip?: ReactNode
    disabled?: boolean
}

const MEDIA_FIELD_SOURCE_KINDS: readonly MediaFieldSourceKind[] = [
    'upload',
    'domain-default',
    'dynamic-macro',
    'fixed-macro',
    'none',
]

/**
 * Narrows a raw string value back to a `MediaFieldSourceKind` without
 * a cast. Returns `undefined` for any unexpected value so callers can ignore it safely.
 */
export function asMediaFieldSourceKind(value: string | number): MediaFieldSourceKind | undefined {
    return MEDIA_FIELD_SOURCE_KINDS.find((kind) => kind === value)
}
